import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  TableContainer,
  TablePagination,
} from "@mui/material";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { CustomFormField } from "../../utilis/component/CustomFormElement";
import TableComponent from "../component/TableComponent";
import { useForm } from "react-hook-form";
import {
  reconcilationlistexcelService,
  reconcilationReportTable,
} from "./services";
import useRepotFile from "../hooks/reportFileHook";
import { useMutation, useQuery } from "react-query";
import {
  getInsuranceByParams,
  getInsuranceType as getAllinsuranceType,
} from "../../Insurance/services";
import { searchFields } from "../../Insurance/ClaimListing/fieldData";
import useAuthentication from "@hooks/useAuthentication";
import { defaultValues, ReconcilationReportFormType } from "./types";
import moment from "moment";
import { clinicId } from "../../constants";
import { useOutletContext } from "react-router-dom";

type ChangeParamsType = {
  target: { value: any; name: string };
  name: string;
};

// add this at the top of ReconcilationReport.tsx
type LayoutContext = {
  open: boolean;
};

const ReconcilationReport: FC = () => {
  const { handleSubmit, control, reset, resetField, watch, getValues } =
    useForm<ReconcilationReportFormType>({ defaultValues });
  const { user } = useAuthentication();

  const insType = watch("insuranceType");
  const isAllTicked = watch("all");

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    mutate: getReconsilationReportTable,
    data: reconcilationReportData,
    isLoading: reconcilationReportTableDataLoading,
    reset: resetMutation,
  } = useMutation(reconcilationReportTable);

  const { data: insuranceType, isLoading: insuranceTypeLoading } = useQuery(
    ["insuranceTypeInAgingReport"],
    getAllinsuranceType,
    { refetchOnWindowFocus: false },
  );

  const {
    mutate: getReceiverByType,
    data: receiver,
    isLoading: receiverLoadig,
  } = useMutation(getInsuranceByParams);

  const {
    mutate: getPayerByType,
    data: payer,
    isLoading: payerLoadig,
  } = useMutation(getInsuranceByParams);

  // Initial load → fetch today's data
  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    getReconsilationReportTable({
      startDate: today,
      endDate: today,
      clinicId,
      tenantClinicId: user?.tenantClinicId,
    });
    reset({
      startDate: today,
      endDate: today,
    });
  }, []);

  const clearField = (fields: (keyof typeof defaultValues)[]) => {
    fields.forEach((f) => resetField(f));
  };

  const handleInsurnceTypeChange = (params: ChangeParamsType) => {
    const { value } = params.target;
    clearField(["payer", "receiver"]);
    const allData = watch();
    if (Boolean(value)) {
      if (value.id === 1) {
        getReceiverByType({ typeId: value.id });
      } else {
        getPayerByType({ typeId: value.id });
      }
      reset({ ...allData });
    } else {
      reset({ ...allData, [searchFields.insuranceType.name]: null });
    }
  };

  const handleApiCall = ({
    value,
    type,
  }: {
    value?: ReconcilationReportFormType;
    type: "form" | "pdf";
  }) => {
    const data = type === "form" && value ? value : getValues();

    const formData = {
      startDate: data?.startDate
        ? moment(data.startDate).format("YYYY-MM-DD")
        : null,
      endDate: data?.endDate ? moment(data.endDate).format("YYYY-MM-DD") : null,
      insuranceType: data?.insuranceType?.name || null, // send name not id

      // If TPA (id=1) → use receiver name, else → use payer name
      receiver:
        data?.insuranceType?.id === 1
          ? data?.receiver?.name || null
          : data?.payer?.name || null,

      payer: data?.payer?.name || null,
      clinicId: Number(user?.clinicId),
      tenantClinicId: user?.tenantClinicId,
    };

    console.log("API payload:", formData);
    getReconsilationReportTable(formData);
  };

  const { open } = useOutletContext<LayoutContext>();

  function onSubmit(value: ReconcilationReportFormType) {
    handleApiCall({ value, type: "form" });
  }

  const handleReset = () => {
    resetMutation();
    reset(defaultValues);
  };

  const filteredData = useMemo(() => {
    const allData = reconcilationReportData?.response || [];
    const { startDate, endDate, insuranceType, receiver, payer } = getValues();

    const filtered = allData.filter((row: any) => {
      let match = true;

      // 📅 Date filter
      if (startDate && endDate && row.createdByDate) {
        const rowDate = moment(row.createdByDate, "DD-MM-YYYY HH:mm:ss");
        match =
          match &&
          rowDate.isBetween(
            moment(startDate, "YYYY-MM-DD"),
            moment(endDate, "YYYY-MM-DD"),
            "day",
            "[]",
          );
      }

      // 🏥 Receiver/Payer filter (only if selected)
      if (insuranceType?.id === 1 && receiver?.name) {
        match =
          match &&
          row.insuranceOrTPA?.trim().toLowerCase() ===
            receiver.name.trim().toLowerCase();
      }

      if (insuranceType?.id !== 1 && payer?.name) {
        match =
          match &&
          row.insuranceOrTPA?.trim().toLowerCase() ===
            payer.name.trim().toLowerCase();
      }

      return match;
    });

    return filtered.map((row: any) => ({
      ...row,
      assInsurance: row.assInsurance || null,
    }));
  }, [
    reconcilationReportData,
    watch("startDate"),
    watch("endDate"),
    watch("insuranceType"),
    watch("receiver"),
    watch("payer"),
  ]);

  console.log("Filtered rows:", filteredData);

  const {
    mutate: getreconcilationReportexcel,
    isLoading: reconcilationexcelLoading,
  } = useRepotFile({
    apiFunction: reconcilationlistexcelService,
    fileType: "xlsx",
  });

  const getFiles = () => {
    const data = getValues();
    const payload = {
      startDate: data?.startDate
        ? moment(data.startDate).format("YYYY-MM-DD")
        : null,
      endDate: data?.endDate ? moment(data.endDate).format("YYYY-MM-DD") : null,
      insuranceType: data?.insuranceType?.name || null,

      // If insuranceType is TPA → send receiver, else → send payer (assInsurance)
      receiver:
        data?.insuranceType?.id === 1
          ? data?.receiver?.name || null
          : data?.payer?.name || null,

      payer: data?.payer?.name || null,
      clinicId: Number(user?.clinicId),
    };

    console.log("🚀 Excel Payload:", payload);

    getreconcilationReportexcel(payload);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (_: unknown, newPage: number) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
      tableContainerRef.current.scrollLeft = 0;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
      tableContainerRef.current.scrollLeft = 0;
    }
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end).map((row: any, idx: any) => ({
      ...row,
      sl: start + idx + 1, // optional: adds a running serial number
    }));
  }, [filteredData, page, rowsPerPage]);

  console.log("payer:vv", payer?.response);

  useEffect(() => {
    // Add class when component mounts
    document.body.classList.add("hide-scrollbar");

    // Remove class when component unmounts
    return () => {
      document.body.classList.remove("hide-scrollbar");
    };
  }, []);

  //  const regionValue= user?.region;
  //    const idHeading =
  //   regionValue === "QATAR_REGION"
  //     ? "Qatar ID"
  //     : "Emirates Id";

  const idHeading = "Civil ID";

  const heading = [
    { heading: "Insurance/TPA", key: "insuranceOrTPA" },
    { heading: "Insurance Name", key: "assInsurance" },
    { heading: "Network", key: "network" },
    { heading: "Plan", key: "plan" },
    { heading: idHeading, key: "emiratesId" },
    { heading: "Patient ID", key: "patientId" },
    { heading: "Patient Name", key: "patientName" },
    { heading: "Mob Number", key: "number" },
    { heading: "Claim Id", key: "claimId" },
    { heading: "Encounter ID", key: "encounterId" },
    { heading: "CPT Code", key: "cptCode" },
    { heading: "CPT Name", key: "cptName" },
    { heading: "Submission Status", key: "submissionStatus" },
    { heading: "Submission Type", key: "submissionType" },
    { heading: "Denial Code", key: "denialCode" },
    { heading: "Denial Description", key: "denialDescription" },

    {
      heading: "Submission Date",
      key: "createdByDate",
      date: "DD-MM-YYYY hh:mm A",
      format: "DD/MM/YYYY HH:mm A",
    },
    { heading: "Cheque No", key: "checkno" },
    { heading: "Settlement No", key: "settleno" },
    { heading: "Insurance Share", key: "insShare" },
    { heading: "Patient Share", key: "patShare" },
    { heading: "Received Amount", key: "receivedAmt" },
    { heading: "Balance", key: "balance" },
  ];
  return (
    <div className="px-3 py-4 flex flex-col gap-7">
      <Paper>
        <div className="p-4 border-b">
          <Typography color="primary" fontWeight={600} fontSize="1rem">
            Reconciliation Report
          </Typography>
        </div>

        <form
          className="grid grid-cols-12 gap-3 p-4"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* From Date */}
          <div className="col-span-3">
            <CustomFormField
              control={control}
              element="datepicker"
              name="startDate"
              fieldProps={{ label: "From Date" }}
              rules={{ required: "From date required" }}
            />
          </div>

          {/* To Date */}
          <div className="col-span-3">
            <CustomFormField
              control={control}
              element="datepicker"
              name="endDate"
              fieldProps={{ label: "To Date" }}
              rules={{ required: "To date required" }}
            />
          </div>

          {/* Insurance Type */}
          <div className="col-span-3">
            <CustomFormField
              name="insuranceType"
              control={control}
              options={insuranceType?.response || []}
              loading={insuranceTypeLoading}
              element="autocomplete"
              fieldProps={{ label: "Insurance Type" }}
              autocompleteProps={{
                isOptionEqualToValue: (option, value) => option.id === value.id,
                getOptionLabel: (option) => option.name,
              }}
              rules={{ onChange: handleInsurnceTypeChange }}
            />
          </div>

          {/* Receiver */}
          {insType?.id === 1 && (
            <div className="col-span-3">
              <CustomFormField
                control={control}
                options={receiver?.response || []}
                loading={receiverLoadig}
                name="receiver"
                fieldProps={{ label: "Receiver" }}
                element="autocomplete"
                autocompleteProps={{
                  isOptionEqualToValue: (option, value) =>
                    option.id === value.id,
                  getOptionLabel: (option) => option.name,
                }}
                rules={{
                  onChange: () => {
                    clearField(["payer"]);
                    getPayerByType({ typeId: 2 });
                  },
                }}
              />
            </div>
          )}

          {/* Payer */}
          <div className="col-span-3">
            <CustomFormField
              name="payer"
              control={control}
              options={payer?.response || []}
              loading={payerLoadig}
              element="autocomplete"
              autocompleteProps={{
                isOptionEqualToValue: (option, value) => option.id === value.id,
                getOptionLabel: (option) => option.name,
              }}
              fieldProps={{ label: insType?.id === 1 ? "Payer" : "Insurance" }}
              rules={{ required: isAllTicked }}
            />
          </div>

          {/* Buttons */}
          <div className="col-span-12 flex justify-end gap-2">
            <Button
              disabled={reconcilationReportTableDataLoading}
              endIcon={
                reconcilationReportTableDataLoading && (
                  <CircularProgress size={18} />
                )
              }
              size="small"
              type="submit"
            >
              Search
            </Button>
            <Button
              onClick={handleReset}
              size="small"
              type="reset"
              color="error"
            >
              Clear
            </Button>
          </div>
        </form>
      </Paper>

      <Paper
        sx={{
          width: "100%",
          maxWidth: open ? "80.6vw" : "94.6vw",
          minWidth: 1200,
          display: "flex",
          flexDirection: "column",
          // height: 670,
        }}
      >
        {/* Toolbar */}
        <div className="flex justify-end gap-3 py-2 px-4 border-b shrink-0">
          <Button
            disabled={reconcilationexcelLoading || !filteredData.length}
            endIcon={
              reconcilationexcelLoading && <CircularProgress size={18} />
            }
            data-filetype="excel"
            onClick={getFiles}
          >
            Download Excel
          </Button>
        </div>

        {/* Scrollable table */}
        <TableContainer
          ref={tableContainerRef}
          sx={{ flex: 1, overflow: "auto" }}
        >
          <TableComponent
            loading={reconcilationReportTableDataLoading}
            data={paginatedData} // ✅ use sliced data here
            tableProps={heading}
            totalCount={filteredData.length} // ✅ total count for pagination
            showPaginationInside={false}
            externalPage={page}
            externalRowsPerPage={rowsPerPage}
          />
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            "& .MuiTablePagination-toolbar": {
              minHeight: "36px", // reduce toolbar height
              height: "36px",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem", // smaller text
              },
            "& .MuiTablePagination-select": {
              fontSize: "0.75rem",
              paddingTop: "2px",
              paddingBottom: "2px",
            },
            "& .MuiTablePagination-actions button": {
              padding: "2px", // smaller pagination buttons
            },
          }}
        />
      </Paper>
    </div>
  );
};

export default ReconcilationReport;
